import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import webSocketManager from '../../utils/WebSocketManager';
import { refreshToken as refreshAccessToken } from '../../utils/authUtils';
import { setAuthToken, clearAuthToken, setServiceWorkerBackendUrl, registerPeriodicSync } from '../../utils/db';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [appAccess, setAppAccess] = useState({});
  const [token, setToken] = useState(localStorage.getItem('token'));
  const DOMAIN = process.env.REACT_APP_API_URL || `${process.env.REACT_APP_DOMAIN}`
  const API_BASE_URL = DOMAIN
  
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      // Also save to IndexedDB for Service Worker access (in case it's missing)
      setAuthToken(storedToken).catch(() => {});
      // Set backend URL for Service Worker background sync
      setServiceWorkerBackendUrl(API_BASE_URL);
      // Register periodic sync (5 min interval, if supported)
      registerPeriodicSync(5).catch(() => {});
      axios.defaults.baseURL = API_BASE_URL;
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      fetchUserData(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Utility: decode JWT exp (ms) without external deps
  const decodeJwtExpMs = (jwt) => {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = JSON.parse(typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('utf8'));
      if (!json || !json.exp) return null;
      return json.exp * 1000;
    } catch (e) {
      return null;
    }
  };

  // Scheduler for proactive token refresh and WS reconnection
  const refreshTimerRef = useRef(null);
  useEffect(() => {
    // Clear any previous timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!token) return;

    const expiresAtMs = decodeJwtExpMs(token);
    if (!expiresAtMs) return;

    const now = Date.now();
    const msUntilExpiry = Math.max(0, expiresAtMs - now);
    const oneDayMs = 24 * 60 * 60 * 1000;
    // Refresh 1 day before expiration, or in 1 hour if token close to exp
    const msUntilRefresh = Math.max(0, msUntilExpiry - oneDayMs);

    const scheduleIn = Math.min(msUntilRefresh || 0, msUntilExpiry);
    const delay = Number.isFinite(scheduleIn) && scheduleIn > 0 ? scheduleIn : Math.min(msUntilExpiry, oneDayMs);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          setToken(newToken);
          localStorage.setItem('token', newToken);
          // Also save to IndexedDB for Service Worker access
          setAuthToken(newToken).catch(() => {});
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          // Force WS reconnections with the new token
          if (webSocketManager && typeof webSocketManager.refreshAllConnectionsWithNewToken === 'function') {
            webSocketManager.refreshAllConnectionsWithNewToken(newToken);
          }
        } else {
          // Refresh failed → logout
          logoutUser();
        }
      } catch (e) {
        logoutUser();
      }
    }, delay);

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token]);

  // PWA CRITICAL: Check token validity when app returns from background
  // setTimeout doesn't fire reliably in background, so we need to check on visibility change
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!token) return;
      
      const expiresAtMs = decodeJwtExpMs(token);
      if (!expiresAtMs) return;
      
      const now = Date.now();
      const msUntilExpiry = expiresAtMs - now;
      const tenMinutesMs = 10 * 60 * 1000;
      
      // If token expires in less than 10 minutes (or already expired), refresh immediately
      if (msUntilExpiry < tenMinutesMs) {
        console.log('[AuthContext] 📱 Token close to expiry after background, refreshing proactively');
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            setToken(newToken);
            localStorage.setItem('token', newToken);
            // Also save to IndexedDB for Service Worker access
            setAuthToken(newToken).catch(() => {});
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            if (webSocketManager && typeof webSocketManager.refreshAllConnectionsWithNewToken === 'function') {
              webSocketManager.refreshAllConnectionsWithNewToken(newToken);
            }
          } else {
            logoutUser();
          }
        } catch (e) {
          console.error('[AuthContext] Token refresh failed after visibility change:', e);
          logoutUser();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token]);

  const fetchUserData = async (authToken) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user_management/api/users/me/`);
      const userData = response.data;
      setUser(userData);
      setUserRole(userData.role);
      
      // Transform app_access array to object map if it's an array
      let appAccessMap = {};
      if (Array.isArray(userData.app_access)) {
        userData.app_access.forEach(access => {
          if (access && access.app) {
            appAccessMap[access.app] = access;
          }
        });
      } else {
        appAccessMap = userData.app_access || {};
      }
      
      setAppAccess(appAccessMap);
    } catch (error) {
      console.error('Error fetching user data:', error);
      logoutUser();
    } finally {
      setLoading(false);
    }
  };

  const loginUser = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/token/`, { username, password });
      const newToken = response.data.access;
      localStorage.setItem('token', newToken);
      // Also save to IndexedDB for Service Worker access
      setAuthToken(newToken).catch(() => {});
      // Set backend URL for Service Worker background sync
      setServiceWorkerBackendUrl(API_BASE_URL);
      // Register periodic sync (5 min interval, if supported)
      registerPeriodicSync(5).catch(() => {});
      setToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      await fetchUserData(newToken);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logoutUser = () => {
    setUser(null);
    setUserRole(null);
    setAppAccess({}); // Clear app access permissions
    setToken(null);
    localStorage.removeItem('token');
    // Also clear from IndexedDB
    clearAuthToken().catch(() => {});
    delete axios.defaults.headers.common['Authorization'];

    // Close all active WebSocket connections to avoid data leakage between sessions
    if (webSocketManager && typeof webSocketManager.cleanup === 'function') {
      webSocketManager.cleanup();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, userRole, appAccess, token }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;